// Left-column controls for a track row: name input, kind chip, mute/solo/delete,
// sync + drumify + instrument + chordify + edit + edit-notes buttons, and volume.
// Split out of TrackRow.jsx to keep each file focused on one visual region.
import React from "react";
import {
  Trash, SpeakerSimpleHigh, SpeakerSimpleSlash,
  Pulse, MagicWand, CaretDown, PencilSimple, PianoKeys, Crosshair, CopySimple,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DRUM_KITS, INSTRUMENTS, CHORD_INSTRUMENTS } from "@/lib/audio";
import { GRID_DIVISIONS, kindOf } from "./constants";

export default function TrackSidebar({
  track, onDelete, onChange,
  canSync, onSync, syncing,
  canDrumify, onDrumify, onOneTapGroove, drumifying,
  canInstrument, onInstrument, onChordify,
  canEdit, onEdit,
  loopBars, onCopySection,
  canNudge, nudgeMode, setNudgeMode,
  onSnapAllNotes,
  busy,
}) {
  const k = kindOf(track.kind);
  return (
    <div className="col-span-12 md:col-span-3 p-4 border-r border-[#34373B] flex items-start gap-3">
      <div className="w-1.5 self-stretch rounded-full" style={{ background: k.c, minHeight: 60 }} />
      <div className="flex-1 min-w-0">
        <Input
          value={track.name}
          onChange={(e) => onChange({ name: e.target.value })}
          className="bg-transparent border-none p-0 h-auto font-display text-xl tracking-tighter text-white focus-visible:ring-0 placeholder:text-[#5A5E63]"
          data-testid={`track-name-${track.id}`}
        />
        <div className="flex items-center gap-2 mt-1 text-xs font-mono uppercase tracking-widest flex-wrap" style={{ color: k.c }}>
          <span>● {k.l} · {track.duration.toFixed(1)}s</span>
          {track.is_synced && (
            <span
              data-testid={`track-synced-chip-${track.id}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#A8FF1F]/50 text-[#A8FF1F] bg-[#A8FF1F]/10 normal-case"
            >
              <Pulse size={10} weight="bold" /> 1/{track.grid_division || 16} sync
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <button
            type="button" onClick={() => onChange({ muted: !track.muted })}
            className={`text-xs uppercase font-mono tracking-widest px-3 py-1 rounded-full border transition-colors ${track.muted ? "border-[#E11D48] text-[#E11D48] bg-[#E11D48]/10" : "border-[#34373B] text-[#9CA0A6] hover:text-white hover:border-white"}`}
            data-testid={`track-mute-${track.id}`}
          >
            {track.muted ? <SpeakerSimpleSlash size={11} className="inline mr-1" /> : <SpeakerSimpleHigh size={11} className="inline mr-1" />}
            Mute
          </button>
          <button
            type="button" onClick={() => onChange({ solo: !track.solo })}
            className={`text-xs uppercase font-mono tracking-widest px-3 py-1 rounded-full border transition-colors ${track.solo ? "border-[#FBBF24] text-[#FBBF24] bg-[#FBBF24]/10" : "border-[#34373B] text-[#9CA0A6] hover:text-white hover:border-white"}`}
            data-testid={`track-solo-${track.id}`}
          >
            Solo
          </button>
          <button
            type="button" onClick={onDelete}
            className="ml-auto text-[#5A5E63] hover:text-[#E11D48] transition-colors"
            aria-label="Delete track" data-testid={`track-delete-${track.id}`}
          >
            <Trash size={16} weight="bold" />
          </button>
        </div>
        {canSync && (
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button" onClick={() => onSync(16)} disabled={syncing}
              data-testid={`track-sync-${track.id}`}
              className="flex-1 text-xs uppercase font-mono tracking-widest px-3 py-2 rounded-full border border-[#A8FF1F] text-[#A8FF1F] hover:bg-[#A8FF1F]/10 disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors"
            >
              <Pulse size={12} weight="bold" /> {syncing ? "Syncing…" : "Sync"}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  disabled={syncing}
                  data-testid={`track-sync-grid-${track.id}`}
                  className="text-xs uppercase font-mono tracking-widest px-2 py-2 rounded-full border border-[#34373B] text-[#9CA0A6] hover:text-white inline-flex items-center gap-1 transition-colors"
                  title="Grid resolution"
                >
                  Grid <CaretDown size={10} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1A1B1D] border-[#34373B] rounded-2xl text-white font-mono">
                {GRID_DIVISIONS.map((g) => (
                  <DropdownMenuItem
                    key={g.v}
                    onSelect={() => onSync(g.v)}
                    data-testid={`track-sync-grid-${g.v}-${track.id}`}
                    className="cursor-pointer"
                  >
                    {g.l} note
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        {canDrumify && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button" disabled={drumifying || busy}
                data-testid={`track-drumify-${track.id}`}
                className="mt-2 w-full text-xs uppercase font-mono tracking-widest px-3 py-2 rounded-full border border-[#34D399] text-[#34D399] hover:bg-[#34D399]/10 disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors"
              >
                <MagicWand size={12} weight="fill" /> {drumifying ? "Drumifying…" : "Drumify"} <CaretDown size={10} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#1A1B1D] border-[#34373B] rounded-2xl text-white font-mono">
              {DRUM_KITS.map((kit) => (
                <DropdownMenuItem
                  key={kit.v}
                  onSelect={() => onDrumify(kit.v)}
                  data-testid={`track-drumify-kit-${kit.v}-${track.id}`}
                  className="cursor-pointer"
                >
                  {kit.l}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {canDrumify && (
          <button
            type="button"
            onClick={onOneTapGroove}
            disabled={drumifying || busy}
            data-testid={`track-one-tap-groove-${track.id}`}
            className="mt-2 w-full text-xs uppercase font-mono tracking-widest px-3 py-2 rounded-full border border-[#FBBF24] text-[#FBBF24] hover:bg-[#FBBF24]/10 disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors"
            title="Build a full groove — 808 kick + snare + hi-hat — in one tap"
          >
            <MagicWand size={12} weight="fill" /> {drumifying ? "Building…" : "One-tap groove"}
          </button>
        )}
        {canInstrument && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button" disabled={busy}
                data-testid={`track-instrument-${track.id}`}
                className="mt-2 w-full text-xs uppercase font-mono tracking-widest px-3 py-2 rounded-full border border-[#60A5FA] text-[#60A5FA] hover:bg-[#60A5FA]/10 disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors"
              >
                <PianoKeys size={12} weight="fill" /> {busy ? "Synthing…" : "Instrument"} <CaretDown size={10} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#1A1B1D] border-[#34373B] rounded-2xl text-white font-mono">
              {INSTRUMENTS.map((inst) => (
                <DropdownMenuItem
                  key={inst.v}
                  onSelect={() => onInstrument(inst.v)}
                  data-testid={`track-instrument-${inst.v}-${track.id}`}
                  className="cursor-pointer"
                >
                  {inst.l}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {canInstrument && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button" disabled={busy}
                data-testid={`track-chordify-${track.id}`}
                className="mt-2 w-full text-xs uppercase font-mono tracking-widest px-3 py-2 rounded-full border border-[#F472B6] text-[#F472B6] hover:bg-[#F472B6]/10 disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors"
              >
                <PianoKeys size={12} weight="fill" /> {busy ? "Chord-ing…" : "Chordify"} <CaretDown size={10} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#1A1B1D] border-[#34373B] rounded-2xl text-white font-mono">
              {CHORD_INSTRUMENTS.map((inst) => (
                <DropdownMenuItem
                  key={`chord-${inst.v}`}
                  onSelect={() => onChordify(inst.v)}
                  data-testid={`track-chordify-${inst.v}-${track.id}`}
                  className="cursor-pointer"
                >
                  {inst.l}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {canEdit && (
          <button
            type="button" onClick={onEdit} disabled={busy}
            data-testid={`track-edit-${track.id}`}
            className="mt-2 w-full text-xs uppercase font-mono tracking-widest px-3 py-2 rounded-full border border-[#FBBF24] text-[#FBBF24] hover:bg-[#FBBF24]/10 disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors"
          >
            <PencilSimple size={12} weight="bold" /> Edit bars
          </button>
        )}
        {loopBars === 8 && !track.is_synced && track.buffer && onCopySection && (
          <button
            type="button" onClick={onCopySection} disabled={busy}
            data-testid={`track-copy-section-${track.id}`}
            title="Duplicate the first four bars onto bars 5–8 to fill the extended loop"
            className="mt-2 w-full text-xs uppercase font-mono tracking-widest px-3 py-2 rounded-full border border-[#34D399] text-[#34D399] hover:bg-[#34D399]/10 disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors"
          >
            <CopySimple size={12} weight="bold" /> Copy 1–4 → 5–8
          </button>
        )}
        {canNudge && (
          <button
            type="button"
            onClick={() => setNudgeMode((v) => !v)}
            disabled={busy}
            data-testid={`track-nudge-toggle-${track.id}`}
            className={`mt-2 w-full text-xs uppercase font-mono tracking-widest px-3 py-2 rounded-full border disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors ${
              nudgeMode
                ? "border-[#C084FC] text-[#0A0A0A] bg-[#C084FC]"
                : "border-[#C084FC] text-[#C084FC] hover:bg-[#C084FC]/10"
            }`}
            title="Drag any note marker onto the nearest 1/16 grid line, or hover for delete/copy"
          >
            <Crosshair size={12} weight="bold" /> {nudgeMode ? "Edit mode ON" : "Edit notes"}
          </button>
        )}
        {canNudge && (
          <button
            type="button"
            onClick={onSnapAllNotes}
            disabled={busy}
            data-testid={`track-snap-all-${track.id}`}
            className="mt-2 w-full text-xs uppercase font-mono tracking-widest px-3 py-2 rounded-full border border-[#A8FF1F] text-[#A8FF1F] hover:bg-[#A8FF1F]/10 disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors"
            title={`Snap every note to the nearest 1/${track.grid_division || 16} grid line`}
          >
            <Pulse size={12} weight="bold" /> Snap · 1/{track.grid_division || 16}
          </button>
        )}
        <div className="mt-3 flex items-center gap-3">
          <span className="text-[#9CA0A6] text-xs uppercase tracking-widest font-mono">Vol</span>
          <Slider
            defaultValue={[track.volume * 100]} min={0} max={100} step={1}
            onValueChange={(v) => onChange({ volume: v[0] / 100 })}
            className="flex-1" data-testid={`track-volume-${track.id}`}
          />
          <span className="font-mono text-xs text-[#9CA0A6] w-7 text-right tabular-nums">{Math.round(track.volume * 100)}</span>
        </div>
        {(track.kind === "melody" || track.kind === "vocal" || track.kind === "drums") && (
          <div
            className="mt-2 flex items-center gap-3"
            title="Reverb — dry (0) to fully wet (100). Applies to this track only."
          >
            <span className="text-[#60A5FA] text-xs uppercase tracking-widest font-mono">Rev</span>
            <Slider
              defaultValue={[Math.round(track.reverb || 0)]} min={0} max={100} step={1}
              onValueChange={(v) => onChange({ reverb: v[0] })}
              className="flex-1" data-testid={`track-reverb-${track.id}`}
            />
            <span className="font-mono text-xs text-[#9CA0A6] w-7 text-right tabular-nums">{Math.round(track.reverb || 0)}</span>
          </div>
        )}
        {(track.kind === "melody" || track.kind === "vocal" || track.kind === "drums") && (
          <div className="mt-1.5 flex items-center gap-1.5">
            {(track.kind === "vocal" ? ["voice", "room", "hall", "plate"] : ["room", "hall", "plate"]).map((rt) => {
              const active = (track.reverb_type || (track.kind === "vocal" ? "voice" : track.kind === "drums" ? "room" : "hall")) === rt;
              return (
                <button
                  key={rt}
                  type="button"
                  onClick={() => onChange({ reverb_type: rt })}
                  data-testid={`track-reverbtype-${rt}-${track.id}`}
                  title={{ voice: "Voice — vocal-tuned room: warm, short, keeps words clear", room: "Room — tight, small space", hall: "Hall — long, lush decay", plate: "Plate — short, bright, metallic" }[rt]}
                  className={`text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border transition-colors ${
                    active
                      ? "bg-[#60A5FA]/15 border-[#60A5FA] text-[#60A5FA]"
                      : "border-[#34373B] text-[#9CA0A6] hover:text-white"
                  }`}
                >
                  {rt}
                </button>
              );
            })}
          </div>
        )}
        {track.kind === "melody" && (
          <div
            className="mt-2 flex items-center gap-3"
            title="Drive — clean (0) to full overdrive (100). Adds tube-style crunch to this track."
          >
            <span className="text-[#FBBF24] text-xs uppercase tracking-widest font-mono">Drv</span>
            <Slider
              defaultValue={[Math.round(track.drive || 0)]} min={0} max={100} step={1}
              onValueChange={(v) => onChange({ drive: v[0] })}
              className="flex-1" data-testid={`track-drive-${track.id}`}
            />
            <span className="font-mono text-xs text-[#9CA0A6] w-7 text-right tabular-nums">{Math.round(track.drive || 0)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
